import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  CheckCircle2, 
  Clock, 
  AlertTriangle, 
  TrendingUp,
  ListTodo,
  Target,
  Zap
} from 'lucide-react';
import TaskBoard from '../components/TaskBoard';
import { tasksApi, TaskStats } from '../lib/api';
import { cn } from '../lib/utils';

export default function Tasks() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as 'en' | 'ar';
  const [stats, setStats] = useState<TaskStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await tasksApi.getStats();
        setStats(response.stats);
      } catch {
        // Stats are optional
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const statCards = stats ? [
    {
      label: lang === 'ar' ? 'إجمالي المهام' : 'Total Tasks',
      value: stats.total,
      icon: ListTodo,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
    },
    {
      label: lang === 'ar' ? 'قيد التنفيذ' : 'In Progress',
      value: stats.inProgress,
      icon: Clock,
      color: 'text-yellow-500',
      bgColor: 'bg-yellow-500/10',
    },
    {
      label: lang === 'ar' ? 'منجز' : 'Completed',
      value: stats.done,
      icon: CheckCircle2,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
    },
    {
      label: lang === 'ar' ? 'عاجل' : 'Urgent',
      value: stats.urgent,
      icon: AlertTriangle,
      color: 'text-red-500',
      bgColor: 'bg-red-500/10',
    },
    {
      label: lang === 'ar' ? 'متأخر' : 'Overdue',
      value: stats.overdue,
      icon: Zap,
      color: 'text-orange-500',
      bgColor: 'bg-orange-500/10',
    },
    {
      label: lang === 'ar' ? 'هذا الأسبوع' : 'This Week',
      value: stats.completedThisWeek,
      icon: TrendingUp,
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10',
    },
  ] : [];

  return (
    <div className="notion-container animate-fade-in">
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="notion-heading-1 mb-2 flex items-center gap-3">
          <Target className="h-8 w-8 text-primary" />
          {lang === 'ar' ? 'المهام' : 'Tasks'}
        </h1>
        <p className="text-muted-foreground">
          {lang === 'ar' 
            ? 'إدارة ومتابعة مهامك ومشاريعك' 
            : 'Manage and track your tasks and projects'}
        </p>
      </div>

      {/* Stats Dashboard */}
      {!loading && stats && stats.total > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
          {statCards.map((stat) => (
            <div
              key={stat.label}
              className={cn(
                'p-4 rounded-xl border transition-all hover:shadow-md',
                stat.bgColor
              )}
            >
              <div className="flex items-center gap-2 mb-2">
                <stat.icon className={cn('h-4 w-4', stat.color)} />
                <span className="text-xs text-muted-foreground">{stat.label}</span>
              </div>
              <div className={cn('text-2xl font-bold', stat.color)}>
                {stat.value}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Progress bar if tasks exist */}
      {stats && stats.total > 0 && (
        <div className="mb-8">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-muted-foreground">
              {lang === 'ar' ? 'التقدم' : 'Progress'}
            </span>
            <span className="font-medium">
              {Math.round((stats.done / stats.total) * 100)}%
            </span>
          </div>
          <div className="h-2 bg-secondary rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-green-400 to-green-600 transition-all duration-500"
              style={{ width: `${(stats.done / stats.total) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Task Board */}
      <TaskBoard />
    </div>
  );
}
